import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Edge, Position } from 'reactflow';
import { Node as ReactFlowNode } from 'reactflow';
import useStore, { CustomReactFlowNode } from '../store';
import { Node as INode, NodeEvent } from '../peregrineFlowInterfaces';
import { socket } from '../socket';
import { shallow } from 'zustand/shallow';

const PASTE_OFFSET = 40;
const MAX_HISTORY = 50;

interface HistoryEntry {
    nodes: ReactFlowNode<CustomReactFlowNode>[];
    edges: Edge[];
}

export function useEditorKeyboard(
    projectToFlow?: (screenPos: { x: number; y: number }) => { x: number; y: number }
) {
    const clipboardRef = useRef<{ nodes: CustomReactFlowNode[], edges: Edge[], externalIncomingEdges: Edge[] } | null>(null);
    const historyStackRef = useRef<HistoryEntry[]>([]);
    const historyIndexRef = useRef<number>(-1);
    const isRestoringRef = useRef(false);
    const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    // Track raw screen mouse position at all times
    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            mousePosRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('mousemove', onMouseMove);
        return () => window.removeEventListener('mousemove', onMouseMove);
    }, []);

    const projectToFlowRef = useRef(projectToFlow);
    useEffect(() => { projectToFlowRef.current = projectToFlow; }, [projectToFlow]);

    const { nodes, edges, setNodes, setEdges } = useStore(
        (state) => ({
            nodes: state.nodes,
            edges: state.edges,
            setNodes: state.setNodes,
            setEdges: state.setEdges,
        }),
        shallow
    );

    // Keep always-fresh refs so stable callbacks can read the latest state
    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);
    useEffect(() => { edgesRef.current = edges; }, [edges]);

    // --------------- History management ---------------

    const pushHistory = useCallback(() => {
        if (isRestoringRef.current) return;
        // Discard any redo branch
        historyStackRef.current.splice(historyIndexRef.current + 1);
        historyStackRef.current.push({
            nodes: [...nodesRef.current],
            edges: [...edgesRef.current],
        });
        if (historyStackRef.current.length > MAX_HISTORY) {
            historyStackRef.current.shift();
        }
        historyIndexRef.current = historyStackRef.current.length - 1;
    }, []); // intentionally stable – reads from refs

    // Push an initial snapshot when the project first loads
    useEffect(() => {
        if (nodes.length > 0 && historyStackRef.current.length === 0) {
            pushHistory();
        }
    }, [nodes.length, pushHistory]);

    // --------------- Apply a history entry (shared by undo/redo) ---------------

    const applyHistoryEntry = useCallback(
        (fromEntry: HistoryEntry, toEntry: HistoryEntry) => {
            isRestoringRef.current = true;
            setNodes(toEntry.nodes as any);
            setEdges(toEntry.edges);
            // Defer clearing the flag so that any effects triggered by the
            // state update (e.g. the node/edge length watcher) still see
            // isRestoringRef === true and skip their pushHistory() call.
            requestAnimationFrame(() => {
                isRestoringRef.current = false;
            });

            const fromNodeIds = new Set(fromEntry.nodes.map((n) => n.id));
            const toNodeIds = new Set(toEntry.nodes.map((n) => n.id));

            // Nodes that disappeared → delete
            fromEntry.nodes
                .filter((n) => !toNodeIds.has(n.id))
                .forEach((n) => {
                    socket.emit('node', { event_type: 'delete', node: n.data } as unknown as NodeEvent);
                });
            // Nodes that were added back → add
            toEntry.nodes
                .filter((n) => !fromNodeIds.has(n.id))
                .forEach((n) => {
                    socket.emit('node', { event_type: 'add', node: n.data } as unknown as NodeEvent);
                });

            const fromEdgeIds = new Set(fromEntry.edges.map((e) => e.id));
            const toEdgeIds = new Set(toEntry.edges.map((e) => e.id));

            // Edges that disappeared → disconnect
            fromEntry.edges
                .filter((e) => !toEdgeIds.has(e.id))
                .forEach((e) => {
                    const src =
                        toEntry.nodes.find((n) => n.id === e.source) ??
                        fromEntry.nodes.find((n) => n.id === e.source);
                    const tgt =
                        toEntry.nodes.find((n) => n.id === e.target) ??
                        fromEntry.nodes.find((n) => n.id === e.target);
                    if (src && tgt && e.sourceHandle && e.targetHandle) {
                        socket.emit('port_disconnect', {
                            event_type: 'disconnect',
                            provider_node: src.data as unknown as INode,
                            provider_port: e.sourceHandle,
                            consumer_node: tgt.data as unknown as INode,
                            consumer_port: e.targetHandle,
                        });
                    }
                });
            // Edges that were added back → connect
            toEntry.edges
                .filter((e) => !fromEdgeIds.has(e.id))
                .forEach((e) => {
                    const src = toEntry.nodes.find((n) => n.id === e.source);
                    const tgt = toEntry.nodes.find((n) => n.id === e.target);
                    if (src && tgt && e.sourceHandle && e.targetHandle) {
                        socket.emit('port_connect', {
                            event_type: 'connect',
                            provider_node: src.data as unknown as INode,
                            provider_port: e.sourceHandle,
                            consumer_node: tgt.data as unknown as INode,
                            consumer_port: e.targetHandle,
                        });
                    }
                });
        },
        [setNodes, setEdges]
    );

    // --------------- Copy ---------------

    const handleCopy = useCallback(() => {
        const selected = useStore.getState().selectedNodes;
        if (selected.length === 0) return;
        const selectedIds = new Set(selected.map((n) => n.id));
        // Internal edges: both endpoints selected (will be duplicated with new IDs)
        const internalEdges = edgesRef.current.filter(
            (e) => selectedIds.has(e.source) && selectedIds.has(e.target)
        );
        // External incoming edges: source outside selection, target inside
        // These are re-emitted on paste so dynamic nodes (Pack/Unpack) receive
        // the type information from their existing upstream connections.
        const externalIncomingEdges = edgesRef.current.filter(
            (e) => !selectedIds.has(e.source) && selectedIds.has(e.target)
        );
        clipboardRef.current = { nodes: selected, edges: internalEdges, externalIncomingEdges };
    }, []);

    // --------------- Paste ---------------

    const handlePaste = useCallback(() => {
        if (useStore.getState().isRunning) return;
        if (!clipboardRef.current) return;

        const { nodes: clipNodes, edges: clipEdges, externalIncomingEdges } = clipboardRef.current;
        const idMap: Record<string, string> = {};

        // Compute bounding-box centre of the copied nodes in flow space
        const xs = clipNodes.map((n) => n.position.x);
        const ys = clipNodes.map((n) => n.position.y);
        const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
        const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;

        // Convert current mouse screen position to flow coordinates
        const mouseFlow = projectToFlowRef.current
            ? projectToFlowRef.current(mousePosRef.current)
            : { x: centerX + PASTE_OFFSET, y: centerY + PASTE_OFFSET };

        // Build new nodes with fresh IDs, placed around the mouse cursor
        const newNodes: ReactFlowNode<CustomReactFlowNode>[] = clipNodes.map((n) => {
            const newId = `${(n.data as unknown as INode).name}-${uuidv4()}`;
            idMap[n.id] = newId;
            const newData = { ...(n.data as unknown as INode), id: newId };
            return {
                ...n,
                id: newId,
                selected: false,
                position: {
                    x: mouseFlow.x + (n.position.x - centerX),
                    y: mouseFlow.y + (n.position.y - centerY),
                },
                targetPosition: n.targetPosition as unknown as Position,
                data: newData as unknown as CustomReactFlowNode,
            };
        });

        // Emit add events and add to store
        newNodes.forEach((n) => {
            socket.emit('node', { event_type: 'add', node: n.data } as unknown as NodeEvent);
        });
        useStore.setState((s) => ({ nodes: [...s.nodes, ...newNodes] }));

        // Rebuild edges between the pasted nodes
        const newEdges: Edge[] = clipEdges
            .map((e) => {
                const ns = idMap[e.source];
                const nt = idMap[e.target];
                if (!ns || !nt) return null;
                const sh = e.sourceHandle?.replace(e.source, ns) ?? null;
                const th = e.targetHandle?.replace(e.target, nt) ?? null;
                return {
                    ...e,
                    id: `e${ns}${sh}-${nt}${th}`,
                    source: ns,
                    target: nt,
                    sourceHandle: sh,
                    targetHandle: th,
                };
            })
            .filter(Boolean) as Edge[];

        // Build external incoming edges: source node unchanged, target remapped to new ID
        const newExternalEdges: Edge[] = externalIncomingEdges
            .map((e) => {
                const nt = idMap[e.target];
                if (!nt) return null;
                const th = e.targetHandle?.replace(e.target, nt) ?? null;
                return { ...e, id: `e${e.source}${e.sourceHandle}-${nt}${th}`, target: nt, targetHandle: th };
            })
            .filter(Boolean) as Edge[];

        const allNewEdges = [...newEdges, ...newExternalEdges];

        if (allNewEdges.length > 0) {
            useStore.setState((s) => ({ edges: [...s.edges, ...allNewEdges] }));
            // Give the backend a moment to register the new nodes before connecting
            setTimeout(() => {
                const currentNodes = useStore.getState().nodes;
                allNewEdges.forEach((edge) => {
                    const src = currentNodes.find((n) => n.id === edge.source);
                    const tgt = currentNodes.find((n) => n.id === edge.target);
                    if (src && tgt && edge.sourceHandle && edge.targetHandle) {
                        socket.emit('port_connect', {
                            event_type: 'connect',
                            provider_node: src.data as unknown as INode,
                            provider_port: edge.sourceHandle,
                            consumer_node: tgt.data as unknown as INode,
                            consumer_port: edge.targetHandle,
                        });
                    }
                });
            }, 100);
        }

        pushHistory();
    }, [pushHistory]);

    // --------------- Undo / Redo ---------------

    const handleUndo = useCallback(() => {
        if (useStore.getState().isRunning) return;
        if (historyIndexRef.current <= 0) return;
        const fromEntry = historyStackRef.current[historyIndexRef.current];
        historyIndexRef.current -= 1;
        const toEntry = historyStackRef.current[historyIndexRef.current];
        applyHistoryEntry(fromEntry, toEntry);
    }, [applyHistoryEntry]);

    const handleRedo = useCallback(() => {
        if (useStore.getState().isRunning) return;
        if (historyIndexRef.current >= historyStackRef.current.length - 1) return;
        const fromEntry = historyStackRef.current[historyIndexRef.current];
        historyIndexRef.current += 1;
        const toEntry = historyStackRef.current[historyIndexRef.current];
        applyHistoryEntry(fromEntry, toEntry);
    }, [applyHistoryEntry]);

    // --------------- Keyboard listener ---------------

    useEffect(() => {
        const isInputFocused = () => {
            const el = document.activeElement as HTMLElement | null;
            return (
                el?.tagName === 'INPUT' ||
                el?.tagName === 'TEXTAREA' ||
                el?.isContentEditable === true
            );
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            if (isInputFocused()) return;
            if (e.key === 'c') {
                e.preventDefault();
                handleCopy();
            } else if (e.key === 'v') {
                e.preventDefault();
                handlePaste();
            } else if (e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            } else if (e.key === 'y' || (e.key === 'Z' && e.shiftKey)) {
                e.preventDefault();
                handleRedo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleCopy, handlePaste, handleUndo, handleRedo]);

    return { pushHistory, isRestoringRef };
}
